import React, { useContext, useState, useEffect, useCallback } from 'react';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import { SocketContext } from '../contexts/SocketContext';
import api from '../utils/api';
import {
  Box,
  Menu,
  MenuItem,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
  Button,
  Snackbar,
  Alert,
  CircularProgress,
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import RefreshIcon from '@mui/icons-material/Refresh';
import Navbar from '../components/Navbar';
import { Kicker, AmberButton, EngGrid, StatusPill } from '../components/design';

const MONO = "'IBM Plex Mono', monospace";

const NodeIcon = ({ color = '#2dd4bf', bg = 'rgba(45,212,191,.08)', border = 'rgba(45,212,191,.28)' }) => (
  <Box sx={{ flexShrink: 0, width: 36, height: 36, borderRadius: '9px', background: bg, border: `1px solid ${border}`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.7">
      <circle cx="6" cy="6" r="2.3" /><circle cx="18" cy="9" r="2.3" /><circle cx="9" cy="18" r="2.3" />
      <path d="M8 7l8 1.6M7.7 16 16.4 10.2" />
    </svg>
  </Box>
);

const ProcessingBar = () => (
  <Box sx={{ position: 'relative', height: '6px', width: '100%', background: 'rgba(120,150,200,.16)', borderRadius: '999px', overflow: 'hidden', mt: '8px' }}>
    <Box sx={{
      position: 'absolute', top: 0, height: '100%', width: '42%', left: '-42%',
      background: 'linear-gradient(90deg,transparent,#2dd4bf,transparent)',
      animation: 'indet 1.4s ease-in-out infinite',
      '@keyframes indet': { '0%': { left: '-42%' }, '100%': { left: '100%' } },
      '@media (prefers-reduced-motion: reduce)': { animation: 'none', left: '29%' },
    }} />
  </Box>
);

const Dashboard = () => {
  const socket = useContext(SocketContext);
  const [drawings, setDrawings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [notificationOpen, setNotificationOpen] = useState(false);
  const [notification, setNotification] = useState({ message: '', severity: 'success' });

  const [openRenameDialog, setOpenRenameDialog] = useState(false);
  const [selectedDrawing, setSelectedDrawing] = useState(null);
  const [newName, setNewName] = useState('');
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [menuDrawingId, setMenuDrawingId] = useState(null);

  const location = useLocation();

  const fetchDrawings = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get('/api/drawings');
      setDrawings(res.data);
      setError(null);
    } catch (err) {
      setError('Failed to fetch drawings');
      console.error('Error fetching drawings:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchDrawings(); }, [fetchDrawings]);

  useEffect(() => {
    if (location.state?.processingStarted) {
      setNotification({ message: location.state.message || `Drawing uploaded and processing started`, severity: 'success' });
      setNotificationOpen(true);
      window.history.replaceState({}, document.title);
    }
  }, [location]);

  const hasProcessing = drawings.some(d => d.status === 'processing');
  useEffect(() => {
    if (!hasProcessing) return;
    const interval = setInterval(async () => {
      try {
        const res = await api.get('/api/drawings');
        setDrawings(res.data);
      } catch (err) {
        console.error('Error polling drawings:', err);
      }
    }, 5000);
    return () => clearInterval(interval);
  }, [hasProcessing]);

  useEffect(() => {
    if (!socket) return;
    const handler = (data) => {
      setDrawings(prev => prev.map(d =>
        d._id === data.drawingId
          ? { ...d, status: data.status || d.status, processingMessage: data.message || d.processingMessage, metadata: { ...d.metadata, processingProgress: data.progress !== undefined ? data.progress : d.metadata?.processingProgress } }
          : d
      ));
    };
    socket.on('drawingProgress', handler);
    return () => socket.off('drawingProgress', handler);
  }, [socket]);

  const handleMenuOpen = (e, drawingId) => { setAnchorEl(e.currentTarget); setMenuDrawingId(drawingId); };
  const handleMenuClose = () => { setAnchorEl(null); setMenuDrawingId(null); };

  const handleRenameDialogOpen = (drawing) => { setSelectedDrawing(drawing); setNewName(drawing.name); setOpenRenameDialog(true); handleMenuClose(); };
  const handleRenameDialogClose = () => { setOpenRenameDialog(false); setSelectedDrawing(null); };
  const handleRename = async () => {
    try {
      const res = await api.put(`/api/drawings/${selectedDrawing._id}`, { name: newName });
      setDrawings(drawings.map(d => d._id === selectedDrawing._id ? res.data : d));
      handleRenameDialogClose();
    } catch (err) { console.error('Error renaming drawing:', err); }
  };

  const handleDeleteDialogOpen = (drawing) => { setSelectedDrawing(drawing); setOpenDeleteDialog(true); handleMenuClose(); };
  const handleDeleteDialogClose = () => { setOpenDeleteDialog(false); setSelectedDrawing(null); };
  const handleDelete = async () => {
    try {
      await api.delete(`/api/drawings/${selectedDrawing._id}`);
      setDrawings(drawings.filter(d => d._id !== selectedDrawing._id));
      handleDeleteDialogClose();
    } catch (err) { console.error('Error deleting drawing:', err); }
  };

  const handleProcessDrawing = async (drawingId) => {
    try {
      await api.post(`/api/drawings/${drawingId}/process`);
      setDrawings(drawings.map(d => d._id === drawingId ? { ...d, status: 'processing', processingMessage: 'Processing started' } : d));
      setNotification({ message: `Processing started for "${drawings.find(d => d._id === drawingId)?.name}"`, severity: 'info' });
      setNotificationOpen(true);
    } catch (err) {
      setNotification({ message: 'Failed to start processing', severity: 'error' });
      setNotificationOpen(true);
    }
  };

  const handleExportData = async (drawingId) => {
    try {
      const res = await api.get(`/api/drawings/${drawingId}/export`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `drawing_data_${drawingId}.csv`);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
      handleMenuClose();
    } catch (err) { console.error('Error exporting data:', err); }
  };

  const processingCount = drawings.filter(d => d.status === 'processing').length;
  const menuDrawing = drawings.find(d => d._id === menuDrawingId);

  // Column layout: Drawing | File | Date | Status | Actions
  const ROW = { display: 'grid', gridTemplateColumns: 'minmax(0,1.7fr) 1.05fr .9fr minmax(176px,210px) 150px', gap: '20px', alignItems: 'start', px: '20px', py: '15px', borderBottom: '1px solid rgba(120,150,200,.12)' };
  const ROW_RESP = { '@media (max-width:820px)': { gridTemplateColumns: 'minmax(0,1fr) minmax(170px,210px) auto' } };

  return (
    <Box sx={{ minHeight: '100vh', background: '#0a1326' }}>
      <Navbar />

      <Box sx={{ position: 'relative', maxWidth: '1180px', mx: 'auto', px: '28px', pt: '40px', pb: '80px' }}>
        <EngGrid opacity={0.4} animate={false} sx={{ inset: '0 0 auto 0', height: '260px' }} />

        {/* Page header */}
        <Box sx={{ position: 'relative', display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', flexWrap: 'wrap', gap: '16px', mb: '30px' }}>
          <Box>
            <Kicker variant="plain" sx={{ mb: '10px' }}>your workspace</Kicker>
            <Box component="h1" sx={{ m: 0, fontSize: '34px', fontWeight: 700, letterSpacing: '-.02em', color: '#f4f8ff' }}>
              P&ID drawings
            </Box>
          </Box>
          <AmberButton to="/upload" sx={{ display: 'inline-flex', alignItems: 'center', gap: '9px', fontSize: '14.5px', py: '12px', px: '22px' }}>
            <Box component="span" sx={{ fontFamily: MONO, fontSize: '16px', lineHeight: 1 }}>+</Box> Upload drawing
          </AmberButton>
        </Box>

        {error && <Alert severity="error" sx={{ mb: '24px', background: 'rgba(244,113,106,.1)', color: '#f4a39e', border: '1px solid rgba(244,113,106,.3)' }}>{error}</Alert>}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '40vh' }}>
            <CircularProgress sx={{ color: '#2dd4bf' }} />
          </Box>
        ) : drawings.length === 0 ? (
          <Box sx={{ border: '1px solid rgba(120,150,200,.22)', borderRadius: '14px', background: 'linear-gradient(180deg,#0d1a33,#0a1326)', p: '64px 28px', textAlign: 'center' }}>
            <Box sx={{ width: '52px', height: '52px', borderRadius: '12px', background: 'rgba(45,212,191,.08)', border: '1px solid rgba(45,212,191,.28)', display: 'flex', alignItems: 'center', justifyContent: 'center', mx: 'auto', mb: '20px' }}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#2dd4bf" strokeWidth="1.7">
                <circle cx="6" cy="6" r="2.3" /><circle cx="18" cy="9" r="2.3" /><circle cx="9" cy="18" r="2.3" />
                <path d="M8 7l8 1.6M7.7 16 16.4 10.2" />
              </svg>
            </Box>
            <Box sx={{ fontSize: '18px', fontWeight: 600, color: '#f4f8ff', mb: '8px' }}>No drawings yet</Box>
            <Box sx={{ fontSize: '15px', color: '#8298bd', mb: '28px', maxWidth: '380px', mx: 'auto' }}>
              Upload a P&ID drawing to start extracting and validating information from it.
            </Box>
            <AmberButton to="/upload">Upload your first drawing <Box component="span" sx={{ fontFamily: MONO }}>→</Box></AmberButton>
          </Box>
        ) : (
          <Box sx={{ position: 'relative', border: '1px solid rgba(120,150,200,.22)', borderRadius: '14px', background: 'linear-gradient(180deg,#0d1a33,#0a1326)', boxShadow: '0 24px 60px rgba(0,0,0,.35)', overflow: 'hidden' }}>
            {/* Title-block bar */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: '20px', py: '13px', borderBottom: '1px solid rgba(120,150,200,.18)', background: 'rgba(8,15,30,.5)' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: '9px', fontFamily: MONO, fontSize: '11.5px', letterSpacing: '.1em', color: '#90a6cc' }}>
                <Box sx={{ width: 8, height: 8, borderRadius: '50%', background: '#2dd4bf' }} />
                DRAWING REGISTER
              </Box>
              <Box sx={{ fontFamily: MONO, fontSize: '11.5px', color: '#5f7398' }}>
                {drawings.length} record{drawings.length !== 1 ? 's' : ''}{processingCount > 0 ? ` · ${processingCount} processing` : ''}
              </Box>
            </Box>

            {/* Column headers */}
            <Box sx={{ ...ROW, ...ROW_RESP, py: '12px', borderBottom: '1px solid rgba(120,150,200,.18)', background: 'rgba(8,15,30,.3)', alignItems: 'center' }}>
              <Box sx={{ fontFamily: MONO, fontSize: '10.5px', letterSpacing: '.12em', textTransform: 'uppercase', color: '#5f7398' }}>Drawing</Box>
              <Box sx={{ fontFamily: MONO, fontSize: '10.5px', letterSpacing: '.12em', textTransform: 'uppercase', color: '#5f7398', display: { xs: 'none', md: 'block' } }}>Source file</Box>
              <Box sx={{ fontFamily: MONO, fontSize: '10.5px', letterSpacing: '.12em', textTransform: 'uppercase', color: '#5f7398', display: { xs: 'none', md: 'block' } }}>Uploaded</Box>
              <Box sx={{ fontFamily: MONO, fontSize: '10.5px', letterSpacing: '.12em', textTransform: 'uppercase', color: '#5f7398' }}>Status</Box>
              <Box />
            </Box>

            {/* Drawing rows */}
            {drawings.map((drawing, idx) => {
              const isProcessed = drawing.status === 'completed';
              const isProcessing = drawing.status === 'processing';
              const isCheckpoint = drawing.status === 'checkpoint';
              const isError = drawing.status === 'error' || drawing.status === 'failed';
              const isUploaded = drawing.status === 'uploaded';
              const isLast = idx === drawings.length - 1;

              const iconColor = isError ? '#f4716a' : isCheckpoint ? '#f5a524' : isProcessed || isProcessing ? '#2dd4bf' : '#8fa3c6';
              const iconBg = isError ? 'rgba(244,113,106,.08)' : isCheckpoint ? 'rgba(245,165,36,.08)' : isProcessed || isProcessing ? 'rgba(45,212,191,.08)' : 'rgba(120,150,200,.07)';
              const iconBorder = isError ? 'rgba(244,113,106,.3)' : isCheckpoint ? 'rgba(245,165,36,.3)' : isProcessed || isProcessing ? 'rgba(45,212,191,.28)' : 'rgba(120,150,200,.22)';

              return (
                <Box
                  key={drawing._id}
                  sx={{ ...ROW, ...ROW_RESP, borderBottom: isLast ? 'none' : '1px solid rgba(120,150,200,.12)', '&:hover': { background: 'rgba(120,150,200,.05)' } }}
                >
                  {/* Drawing cell */}
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: '13px', minWidth: 0 }}>
                    <NodeIcon color={iconColor} bg={iconBg} border={iconBorder} />
                    <Box sx={{ minWidth: 0 }}>
                      <Box sx={{ fontSize: '15px', fontWeight: 500, lineHeight: 1.3, color: '#eaf0fb', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {drawing.name}
                      </Box>
                      <Box sx={{ fontFamily: MONO, fontSize: '11px', color: '#6f86ad', mt: '5px' }}>
                        {drawing.drawingNumber ? `${drawing.drawingNumber} · REV ${drawing.revision || 1}` : '—'}
                      </Box>
                    </Box>
                  </Box>

                  {/* File */}
                  <Box sx={{ fontFamily: MONO, fontSize: '12.5px', color: '#aebfdd', mt: '23px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: { xs: 'none', md: 'block' } }}>
                    {drawing.originalFilename || drawing.filename || '—'}
                  </Box>

                  {/* Date */}
                  <Box sx={{ fontSize: '13px', color: '#8fa3c6', mt: '23px', display: { xs: 'none', md: 'block' } }}>
                    {(drawing.uploadedAt || drawing.createdAt) ? new Date(drawing.uploadedAt || drawing.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—'}
                  </Box>

                  {/* Status */}
                  <Box>
                    {isProcessing ? (
                      <Box sx={{ maxWidth: '230px' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: '7px' }}>
                          <Box sx={{ width: 7, height: 7, borderRadius: '50%', background: '#2dd4bf', animation: 'pulseDot 1.1s ease-in-out infinite', '@keyframes pulseDot': { '0%,100%': { opacity: .35 }, '50%': { opacity: 1 } } }} />
                          <Box sx={{ fontFamily: MONO, fontSize: '11.5px', letterSpacing: '.04em', color: '#7ef0e2' }}>
                            PROCESSING{drawing.metadata?.processingProgress != null ? <Box component="span" sx={{ color: '#5fe6d6', ml: '6px' }}>{drawing.metadata.processingProgress}%</Box> : null}
                          </Box>
                        </Box>
                        <ProcessingBar />
                        {drawing.processingMessage && (
                          <Box sx={{ fontFamily: MONO, fontSize: '10.5px', color: '#6f86ad', mt: '7px' }}>
                            ↳ {drawing.processingMessage}
                          </Box>
                        )}
                      </Box>
                    ) : isCheckpoint ? (
                      <Box>
                        <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: '7px', px: '10px', py: '4px', borderRadius: '999px', border: '1px solid rgba(245,165,36,.4)', background: 'rgba(245,165,36,.10)' }}>
                          <Box sx={{ width: 6, height: 6, borderRadius: '50%', background: '#f5a524', animation: 'pulseDot 1.6s ease-in-out infinite', '@keyframes pulseDot': { '0%,100%': { opacity: .4 }, '50%': { opacity: 1 } } }} />
                          <Box sx={{ fontFamily: MONO, fontSize: '11px', fontWeight: 600, letterSpacing: '.12em', color: '#f5a524' }}>REVIEW NEEDED</Box>
                        </Box>
                        <Box sx={{ fontFamily: MONO, fontSize: '10.5px', color: '#6f86ad', mt: '6px' }}>pipeline paused · awaiting your input</Box>
                      </Box>
                    ) : (
                      <Box>
                        <StatusPill
                          status={isProcessed ? 'processed' : isError ? 'error' : 'uploaded'}
                          label={isProcessed ? 'PROCESSED' : isError ? 'ERROR' : 'UPLOADED'}
                        />
                        {isProcessed && (drawing.metadata?.totalElements || drawing.metadata?.totalConnections) && (
                          <Box sx={{ fontFamily: MONO, fontSize: '11px', color: '#6f86ad', mt: '6px' }}>
                            {drawing.metadata.totalElements} elements{drawing.metadata.totalConnections ? ` · ${drawing.metadata.totalConnections} conn.` : ''}
                          </Box>
                        )}
                        {isError && (
                          <Box sx={{ fontFamily: MONO, fontSize: '11px', color: '#6f86ad', mt: '6px' }}>detection failed</Box>
                        )}
                        {isUploaded && (
                          <Box sx={{ fontFamily: MONO, fontSize: '11px', color: '#6f86ad', mt: '6px' }}>ready to process</Box>
                        )}
                      </Box>
                    )}
                  </Box>

                  {/* Actions */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', justifyContent: 'flex-end' }}>
                    {isProcessed && (
                      <Box
                        component={RouterLink}
                        to={`/drawing/${drawing._id}`}
                        sx={{ textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: '7px', background: 'rgba(45,212,191,.14)', border: '1px solid rgba(45,212,191,.42)', color: '#7ef0e2', fontWeight: 600, fontSize: '13.5px', px: '16px', py: '8px', borderRadius: '8px', transition: 'background .15s, color .15s', '&:hover': { background: 'rgba(45,212,191,.22)', color: '#bff7ee' } }}
                      >
                        Verify <ArrowForwardIcon sx={{ fontSize: '14px' }} />
                      </Box>
                    )}
                    {isCheckpoint && (
                      <Box
                        component={RouterLink}
                        to={`/drawing/${drawing._id}`}
                        sx={{ textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: '7px', background: 'rgba(245,165,36,.14)', border: '1px solid rgba(245,165,36,.5)', color: '#f5a524', fontWeight: 600, fontSize: '13.5px', px: '16px', py: '8px', borderRadius: '8px', transition: 'background .15s, color .15s', '&:hover': { background: 'rgba(245,165,36,.22)', color: '#ffb83e' } }}
                      >
                        Review <ArrowForwardIcon sx={{ fontSize: '14px' }} />
                      </Box>
                    )}
                    {isUploaded && (
                      <Box
                        component="button"
                        onClick={() => handleProcessDrawing(drawing._id)}
                        sx={{ display: 'inline-flex', alignItems: 'center', gap: '7px', background: 'transparent', border: '1px solid rgba(120,150,200,.3)', color: '#dce6fa', fontWeight: 600, fontSize: '13.5px', px: '16px', py: '8px', borderRadius: '8px', cursor: 'pointer', fontFamily: "'Lexend', sans-serif", transition: 'border-color .15s, color .15s', '&:hover': { borderColor: 'rgba(45,212,191,.6)', color: '#fff' } }}
                      >
                        <PlayArrowIcon sx={{ fontSize: '14px' }} /> Process
                      </Box>
                    )}
                    {isError && (
                      <Box
                        component="button"
                        onClick={() => handleProcessDrawing(drawing._id)}
                        sx={{ display: 'inline-flex', alignItems: 'center', gap: '7px', background: 'transparent', border: '1px solid rgba(244,113,106,.42)', color: '#f4a39e', fontWeight: 600, fontSize: '13.5px', px: '16px', py: '8px', borderRadius: '8px', cursor: 'pointer', fontFamily: "'Lexend', sans-serif", transition: 'background .15s, color .15s', '&:hover': { background: 'rgba(244,113,106,.12)', color: '#ffc4bf' } }}
                      >
                        <RefreshIcon sx={{ fontSize: '14px' }} /> Retry
                      </Box>
                    )}

                    {/* ⋯ menu */}
                    <Box
                      component="button"
                      onClick={(e) => handleMenuOpen(e, drawing._id)}
                      sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '34px', height: '34px', borderRadius: '8px', border: '1px solid rgba(120,150,200,.22)', color: '#8fa3c6', background: 'transparent', cursor: 'pointer', transition: 'border-color .15s, color .15s', '&:hover': { borderColor: 'rgba(45,212,191,.4)', color: '#2dd4bf' } }}
                    >
                      <MoreVertIcon sx={{ fontSize: '16px' }} />
                    </Box>
                  </Box>
                </Box>
              );
            })}
          </Box>
        )}
      </Box>

      {/* ⋯ Context Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        sx={{ '& .MuiPaper-root': { background: '#0e1c39', border: '1px solid rgba(120,150,200,.3)', borderRadius: '10px', boxShadow: '0 20px 50px rgba(0,0,0,.5)', minWidth: '184px' } }}
      >
        {menuDrawing && [
          <MenuItem key="rename" onClick={() => handleRenameDialogOpen(menuDrawing)} sx={{ fontSize: '13.5px', color: '#dce6fa', '&:hover': { background: 'rgba(120,150,200,.1)' } }}>Rename</MenuItem>,
          (['uploaded', 'error', 'failed'].includes(menuDrawing.status)) && (
            <MenuItem key="process" onClick={() => { handleProcessDrawing(menuDrawingId); handleMenuClose(); }} sx={{ fontSize: '13.5px', color: '#dce6fa', '&:hover': { background: 'rgba(120,150,200,.1)' } }}>
              {menuDrawing.status === 'uploaded' ? 'Process drawing' : 'Retry processing'}
            </MenuItem>
          ),
          <MenuItem key="export" onClick={() => handleExportData(menuDrawingId)} disabled={menuDrawing.status !== 'completed'} sx={{ fontSize: '13.5px', color: menuDrawing.status === 'completed' ? '#dce6fa' : '#4f648a', '&:hover': { background: menuDrawing.status === 'completed' ? 'rgba(120,150,200,.1)' : 'transparent' } }}>Export data</MenuItem>,
          <Box key="divider" sx={{ height: '1px', background: 'rgba(120,150,200,.16)', mx: '6px', my: '5px' }} />,
          <MenuItem key="delete" onClick={() => handleDeleteDialogOpen(menuDrawing)} sx={{ fontSize: '13.5px', color: '#f4716a', '&:hover': { background: 'rgba(244,113,106,.12)' } }}>Delete</MenuItem>,
        ]}
      </Menu>

      {/* Rename Dialog */}
      <Dialog open={openRenameDialog} onClose={handleRenameDialogClose} PaperProps={{ sx: { background: '#0e1c39', border: '1px solid rgba(120,150,200,.22)', borderRadius: '14px' } }}>
        <DialogTitle sx={{ color: '#f4f8ff' }}>Rename Drawing</DialogTitle>
        <DialogContent>
          <TextField autoFocus margin="dense" label="New Name" type="text" fullWidth value={newName} onChange={(e) => setNewName(e.target.value)} />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleRenameDialogClose} sx={{ color: '#8298bd' }}>Cancel</Button>
          <Button onClick={handleRename} variant="contained" color="secondary" disabled={!newName.trim()}>Save</Button>
        </DialogActions>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={openDeleteDialog} onClose={handleDeleteDialogClose} PaperProps={{ sx: { background: '#0e1c39', border: '1px solid rgba(120,150,200,.22)', borderRadius: '14px' } }}>
        <DialogTitle sx={{ color: '#f4f8ff' }}>Delete Drawing</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ color: '#aebfdd' }}>
            Are you sure you want to delete "{selectedDrawing?.name}"? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteDialogClose} sx={{ color: '#8298bd' }}>Cancel</Button>
          <Button onClick={handleDelete} variant="contained" sx={{ background: '#f4716a', color: '#fff', '&:hover': { background: '#f05a52' } }}>Delete</Button>
        </DialogActions>
      </Dialog>

      {/* Notification */}
      <Snackbar open={notificationOpen} autoHideDuration={6000} onClose={(e, r) => { if (r !== 'clickaway') setNotificationOpen(false); }} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert onClose={() => setNotificationOpen(false)} severity={notification.severity} sx={{ width: '100%' }}>
          {notification.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Dashboard;
